import {
  AnchorButton,
  Divider,
  H6,
  Icon,
  Menu,
  MenuDivider,
  MenuItem,
  Position,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import { getCurrentConnectionInfo } from 'portal-sdk';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import {
  redirectToPortalDashboard,
  redirectToPortalHome,
  redirectToPortalProduct,
  usePortalConnection,
  usePortalConnections,
  usePortalUser,
} from 'vet-bones/utils/portal';

import { TEST_IDS } from 'src/constants/testIds';
import { isConnectedSelector } from 'src/selectors/isConnected';
import * as copy from 'src/templates/copy';
import { icons } from 'src/templates/icons';
import { colors } from 'src/templates/styles/colors';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const AppNavBarTooltipContent: React.VFC<{
  headerText: string;
  children: React.ReactNode;
}> = ({ headerText, children }) => (
  <div data-testid={TEST_IDS.portalConnect.tooltipContent}>
    <H6 className="sd-app-navbar-tooltip-content__header">{headerText}</H6>
    <span>{children}</span>
  </div>
);

export const PortalConnectionButton = () => {
  const isConnected = useReduxSelector(isConnectedSelector);

  const portalUser = usePortalUser();
  const portalConnection = usePortalConnection();
  const portalConnections = usePortalConnections();

  const portalAccountName = portalUser.data?.email || portalUser.data?.username;

  const connectionTooltipContent = (
    <AppNavBarTooltipContent
      headerText={
        copy.components.appNavbar.connectionButton.portal.tooltipHeader
      }
    >
      {portalAccountName}
    </AppNavBarTooltipContent>
  );

  const urlInfo = getCurrentConnectionInfo(window);
  const connectionIndex = urlInfo?.connectionIndex || 0;
  const urlProduct = urlInfo?.product;

  const menu = (
    <Menu data-testid={TEST_IDS.portalConnect.menu}>
      <MenuDivider title={portalAccountName} />

      {portalConnections.data?.map(({ id, name, index, dashboard }) => (
        <MenuItem
          key={id}
          active={index === connectionIndex}
          disabled={!dashboard || !urlProduct}
          icon={icons.connection}
          onClick={() => {
            if (dashboard && index !== connectionIndex && urlProduct) {
              redirectToPortalProduct(window, dashboard, urlProduct);
            }
          }}
          text={name}
        />
      ))}

      <Divider />

      <MenuItem
        icon={IconNames.HOME}
        onClick={() => {
          if (portalConnection?.dashboard) {
            redirectToPortalDashboard(window, portalConnection.dashboard);
          } else {
            redirectToPortalHome(window);
          }
        }}
        text={copy.components.appNavbar.connectionButton.portal.home}
      />
    </Menu>
  );

  return (
    <Popover2
      // since our navbar is dark theme, don't inherit that
      content={menu}
      inheritDarkTheme={false}
    >
      <Tooltip
        content={connectionTooltipContent}
        position={Position.BOTTOM_RIGHT}
      >
        <AnchorButton
          data-testid={TEST_IDS.portalConnect.icon}
          icon={
            <Icon
              color={isConnected ? colors.connected : undefined}
              icon={icons.connection}
            />
          }
          large
          minimal
        />
      </Tooltip>
    </Popover2>
  );
};
